import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Buildings,
  ArrowRight,
  Cpu,
  GraduationCap,
  CalendarCheck,
  Certificate,
  QrCode
} from '@phosphor-icons/react';
import { partnersAPI } from '../services/api';

const internSlides = [
  { src: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&w=1200&q=70', title: 'Code on live projects' },
  { src: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1200&q=70', title: 'Frontend & UI work' },
  { src: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=70', title: 'Weekly team reviews' },
  { src: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1200&q=70', title: 'Learn with mentors' }
];

const internFeatures = [
  { icon: GraduationCap, text: 'Open to all students' },
  { icon: CalendarCheck, text: 'Weekly live assignments' },
  { icon: Certificate, text: 'Offer & completion letters' },
  { icon: QrCode, text: 'QR-verified certificates' }
];

const InternFadeSlide = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setActive((n) => (n + 1) % internSlides.length);
    }, 3800);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="relative h-full min-h-[200px] sm:min-h-[240px] lg:min-h-full overflow-hidden bg-slate-200">
      {internSlides.map((item, index) => (
        <img
          key={item.title}
          src={item.src}
          alt=""
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-out ${
            index === active ? 'opacity-100 intern-fade-zoom' : 'opacity-0'
          }`}
        />
      ))}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/80 to-transparent px-5 py-4">
        <p className="text-sm font-semibold text-white">{internSlides[active]?.title}</p>
      </div>
      <div className="absolute bottom-4 right-4 flex gap-1.5">
        {internSlides.map((item, index) => (
          <button
            key={item.title}
            type="button"
            aria-label={item.title}
            onClick={() => setActive(index)}
            className={`h-1.5 rounded-full smooth-transition ${
              index === active ? 'w-5 bg-white' : 'w-1.5 bg-white/50 hover:bg-white/80'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const HOSTINGER_REFERRAL = 'https://www.hostinger.com/in?REFERRALCODE=GMITS26';

const techPartners = [
  {
    name: 'Meta',
    logo: '/partners/meta.svg',
    description: 'WhatsApp Business API',
    link: 'https://www.facebook.com/business/whatsapp'
  },
  {
    name: 'Hostinger',
    logo: '/partners/hostinger.svg',
    description: '20% off any plan',
    code: 'GMITS26',
    link: HOSTINGER_REFERRAL
  },
  {
    name: 'Infozy',
    logo: '/partners/infozy.svg',
    description: 'Digital Solutions',
    link: null
  }
];

const trustedClients = [
  { name: 'Shivanetra Real Estate', logo: '/partners/shivanetra.png', link: 'https://shivanetraestate.com/' },
  { name: "Dushyant's Realty", logo: '/partners/dushyant-realty.png', link: null },
  { name: 'Panchayat Samiti Satara', logo: null, fallback: '🏘', link: null },
  { name: 'Collector Office Satara', logo: '/logo13.png', link: 'https://satarasaath.in' },
  { name: 'Malkapur Nagarparishad', logo: null, fallback: 'building', link: 'https://malkapurnpcashbook.in' },
  { name: 'Lefko Textile', logo: '/logo11.jpeg', link: 'https://lefko.in' },
  { name: 'Shares Recovery Expert', logo: '/logo12.jpeg', link: 'https://sharesrecoveryexpert.com' }
];

const PartnerCard = ({ name, logo, fallback }) => (
  <div className="h-24 w-full px-4 py-3 rounded-2xl bg-white dark:bg-dark-800 shadow-sm border border-gray-100 dark:border-gray-800 hover:border-brand-300 dark:hover:border-brand-600 hover:shadow-md smooth-transition flex items-center gap-3 overflow-hidden cursor-pointer group">
    {logo ? (
      <img
        src={logo}
        alt={`${name} - Client of Growmore IT Services Satara`}
        loading="lazy"
        className="h-14 w-[4.5rem] object-contain shrink-0 rounded-md bg-white group-hover:scale-105 smooth-transition"
      />
    ) : fallback === 'building' ? (
      <Buildings className="w-10 h-10 text-green-600 shrink-0" weight="duotone" />
    ) : (
      <span className="text-3xl shrink-0">{fallback || '🏢'}</span>
    )}
    <span className="font-semibold text-xs sm:text-sm text-gray-800 dark:text-gray-200 leading-tight group-hover:text-brand-600 dark:group-hover:text-brand-400 smooth-transition">
      {name}
    </span>
  </div>
);

const uniquePartners = (items) => {
  const seen = new Set();
  return (items || []).filter((item) => {
    const key = String(item.name || '').trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const MarqueeRow = ({ items, direction = 'left' }) => {
  if (!items.length) return null;

  const group = (copyKey) => (
    <div className="partner-marquee-group" aria-hidden={copyKey === 'b' ? 'true' : undefined}>
      {items.map((item) => {
        const href = item.link || item.website || '';
        const card = <PartnerCard name={item.name} logo={item.logo || item.logo_url} fallback={item.fallback} />;
        if (!href) {
          return <div key={`${copyKey}-${item.id || item.name}`} className="block">{card}</div>;
        }
        return (
          <a
            key={`${copyKey}-${item.id || item.name}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            {card}
          </a>
        );
      })}
    </div>
  );

  return (
    <div className="relative w-full overflow-hidden py-1">
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 sm:w-12 bg-gradient-to-r from-gray-50 to-transparent dark:from-dark-800" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 sm:w-12 bg-gradient-to-l from-brand-50 to-transparent dark:from-dark-800" />
      <div className={`partner-marquee ${direction === 'right' ? 'is-right' : ''}`}>
        {group('a')}
        {group('b')}
      </div>
    </div>
  );
};

const Clients = () => {
  const [extraPartners, setExtraPartners] = useState([]);

  useEffect(() => {
    partnersAPI.list()
      .then(({ data }) => setExtraPartners(data.partners || []))
      .catch(() => setExtraPartners([]));
  }, []);

  const fallbackPartners = trustedClients;
  const list = uniquePartners([...fallbackPartners, ...extraPartners]);
  const rowOne = list.filter((_, i) => i % 2 === 0);
  const rowTwo = list.filter((_, i) => i % 2 === 1);

  return (
    <section id="clients" className="relative overflow-hidden pt-0 pb-16 md:pb-20 px-4 md:px-6 bg-gradient-to-br from-gray-50 via-white to-brand-50 dark:from-dark-800 dark:via-dark-900 dark:to-dark-800">
      <div className="absolute inset-0 bg-grid-pattern opacity-5 dark:opacity-10 pointer-events-none" />

      <div className="section-max-width relative z-10 overflow-hidden">
        <div className="mb-16 md:mb-20">
          <div className="text-center mb-6 md:mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full badge-primary mb-3 sm:mb-6">
              <Buildings className="w-5 h-5 text-brand-600 dark:text-brand-400" weight="fill" />
              <span className="font-semibold tracking-wide uppercase">Trusted Partners</span>
            </div>
            <h3 className="text-display text-3xl md:text-4xl font-bold mb-3 md:mb-4 text-gray-900 dark:text-white">
              Trusted by
              <span className="text-gradient"> Industry Leaders</span>
            </h3>
            <p className="text-body text-sm sm:text-lg max-w-3xl mx-auto">
              We build cutting-edge tools that solve real-world business challenges with innovative technology and user-centric design.
            </p>
          </div>

          <div className="space-y-3 overflow-hidden">
            <MarqueeRow items={rowOne} direction="left" />
            {rowTwo.length > 0 && <MarqueeRow items={rowTwo} direction="right" />}
          </div>
        </div>

        <div className="intern-card-enter overflow-hidden rounded-2xl sm:rounded-3xl border border-brand-100 dark:border-brand-800 bg-white dark:bg-dark-800 shadow-soft scroll-mt-28 mb-14 md:mb-20">
          <div className="grid lg:grid-cols-2">
            <div className="flex flex-col justify-center gap-5 md:gap-7 px-4 sm:px-10 lg:px-12 py-7 sm:py-10 md:py-12">
              <div className="space-y-2.5">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
                  Paid internships
                </p>
                <h3 className="font-display text-[1.65rem] md:text-3xl font-extrabold text-gray-900 dark:text-white leading-tight">
                  Intern with Growmore.
                </h3>
                <p className="text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                  Live IT work, weekly tasks and official letters — not a dummy certificate.
                </p>
              </div>
              <ul className="grid grid-cols-2 gap-2 sm:gap-3">
                {internFeatures.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.text} className="flex items-start sm:items-center gap-2 sm:gap-3 rounded-xl bg-brand-50/80 dark:bg-brand-900/20 px-3 py-2.5 sm:px-3.5 sm:py-3">
                      <Icon className="w-5 h-5 text-brand-600 dark:text-brand-400 shrink-0 mt-0.5 sm:mt-0" weight="duotone" />
                      <span className="text-xs sm:text-sm font-semibold text-gray-800 dark:text-gray-200 leading-snug">{item.text}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
                <Link to="/internship" className="btn-primary text-white inline-flex items-center justify-center gap-2 w-full sm:w-fit">
                  Explore internships
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/certificates" className="btn-secondary inline-flex items-center justify-center gap-2 w-full sm:w-fit">
                  <Certificate className="w-5 h-5" weight="duotone" />
                  Download certificate
                </Link>
              </div>
            </div>
            <InternFadeSlide />
          </div>
        </div>

        <div id="tech-partners" className="scroll-mt-28">
          <div className="text-center mb-10 md:mb-12">
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full badge-primary mb-6">
              <Cpu className="w-5 h-5 text-brand-600 dark:text-brand-400" weight="fill" />
              <span className="font-semibold tracking-wide uppercase">Tech Partners</span>
            </div>
            <h3 className="text-display text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white leading-tight">
              Our
              <span className="text-gradient"> Technology Partners</span>
            </h3>
            <p className="text-body text-lg max-w-3xl mx-auto leading-relaxed">
              Platforms and partners we work with for WhatsApp API, hosting, digital delivery and IT solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {techPartners.map((partner) => {
              const inner = (
                <>
                  <div className="h-[4.25rem] w-[4.25rem] sm:h-20 sm:w-20 rounded-2xl bg-slate-50 dark:bg-white/5 border border-gray-100 dark:border-gray-700 flex items-center justify-center shrink-0 overflow-hidden">
                    <img
                      src={partner.logo}
                      alt={`${partner.name} logo`}
                      className="h-11 w-11 sm:h-12 sm:w-12 object-contain group-hover:scale-105 smooth-transition"
                    />
                  </div>
                  <div className="min-w-0 space-y-0.5">
                    <h4 className="font-sans font-extrabold text-gray-900 dark:text-white text-lg leading-snug">
                      {partner.name}
                    </h4>
                    {partner.code ? (
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-snug">
                        Code <span className="font-bold text-brand-600 dark:text-brand-400">{partner.code}</span> · {partner.description}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400 leading-snug">
                        {partner.description}
                      </p>
                    )}
                  </div>
                </>
              );
              const cardClass = 'group rounded-2xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-xl hover:border-brand-300 dark:hover:border-brand-600 hover:-translate-y-1 smooth-transition p-5 sm:p-6 flex items-center sm:flex-col sm:text-center gap-4';
              if (!partner.link) {
                return (
                  <div key={partner.name} className={cardClass}>
                    {inner}
                  </div>
                );
              }
              return (
                <a
                  key={partner.name}
                  href={partner.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cardClass}
                >
                  {inner}
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Clients;
